/**
 * PaymentComplete.jsx - Payment completion page
 *
 * Handles both Checkout Session (embedded) and PaymentIntent flows.
 */
import { useState, useEffect } from 'react';

const PaymentComplete = () => {
  const [status, setStatus] = useState('loading');
  const [sessionId, setSessionId] = useState('');
  const [connectedAccount, setConnectedAccount] = useState('');
  const [text, setText] = useState('Loading...');

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    // Checkout Session (embedded) returns session_id
    const checkoutSessionId = urlParams.get('session_id');
    const connectedAccountParam = urlParams.get('connected_account');

    if (checkoutSessionId) {
      setSessionId(checkoutSessionId);
      setConnectedAccount(connectedAccountParam || '');

      // Fetch session status from backend
      const fetchStatus = async () => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          let url = `${backendUrl}/api/public/v1/checkout/status?session_id=${checkoutSessionId}`;
          if (connectedAccountParam) {
            url += `&connected_account=${connectedAccountParam}`;
          }
          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }

          const data = await response.json();

          if (data.status === 'complete') {
            setStatus('succeeded');
            setText('Payment succeeded!');
          } else if (data.status === 'open') {
            setStatus('pending');
            setText('Payment is pending...');
          } else {
            setStatus('unknown');
            setText('Payment status: ' + (data.status || 'unknown'));
          }
        } catch (err) {
          console.error('[PaymentComplete] Failed to fetch session status:', err);
          setStatus('unknown');
          setText('Could not load payment status. Please check your email or Stripe dashboard.');
        }
      };

      fetchStatus();
      return;
    }

    // Fallback: PaymentIntent flow (redirect_status)
    const paymentIntent = urlParams.get('payment_intent');
    const redirectStatus = urlParams.get('redirect_status');

    if (paymentIntent) {
      setSessionId(paymentIntent);
      setConnectedAccount(connectedAccountParam || '');

      switch (redirectStatus) {
        case 'succeeded':
          setStatus('succeeded');
          setText('Payment succeeded!');
          break;
        case 'processing':
          setStatus('processing');
          setText('Your payment is processing.');
          break;
        default:
          setStatus('succeeded');
          setText('Payment completed!');
          break;
      }
      return;
    }

    setStatus('error');
    setText('Missing payment information');
  }, []);

  // TEST: Uncomment to test ErrorBoundary (must be after all hooks)
  // throw new Error('Testing ErrorBoundary on Complete page');

  // Build dashboard URL
  const getDashboardUrl = () => {
    if (!sessionId) return null;

    // For Checkout Sessions, link to the session
    if (sessionId.startsWith('cs_')) {
      if (connectedAccount) {
        return `https://dashboard.stripe.com/connect/accounts/${connectedAccount}/checkout/sessions/${sessionId}`;
      }
      return `https://dashboard.stripe.com/checkout/sessions/${sessionId}`;
    }

    // For PaymentIntents
    if (connectedAccount) {
      return `https://dashboard.stripe.com/connect/accounts/${connectedAccount}/payments/${sessionId}`;
    }
    return `https://dashboard.stripe.com/payments/${sessionId}`;
  };

  const dashboardUrl = getDashboardUrl();

  return (
    <div id="payment-status">
      <h2 id="status-text">{text}</h2>
      {sessionId && (
        <div id="details-table">
          <table>
            <tbody>
              <tr>
                <td className="TableLabel">Session/Payment ID</td>
                <td className="TableContent" style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                  {sessionId}
                </td>
              </tr>
              <tr>
                <td className="TableLabel">Status</td>
                <td className="TableContent">{status}</td>
              </tr>
              {connectedAccount && (
                <tr>
                  <td className="TableLabel">Connected Account</td>
                  <td className="TableContent">{connectedAccount}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {dashboardUrl && (
        <div style={{ marginTop: '20px' }}>
          <a href={dashboardUrl} className="view-details" rel="noopener noreferrer" target="_blank">
            View in Stripe Dashboard
          </a>
        </div>
      )}
    </div>
  );
};

export default PaymentComplete;
